/**
 * VIP Membership Auto-Add
 * ----------------------------------------------------------------------------
 * When "Neloria - Calcium Eye Cream" is in the cart, automatically ensure
 * "VIP Membership" is also in the cart (qty 1). When Neloria is removed,
 * VIP Membership is removed too.
 *
 * The Buy X Get Y automatic discount in Shopify admin handles the pricing —
 * this script does not modify prices.
 *
 * To remove this feature later:
 *   1. Delete this file (assets/vip-auto-add.js).
 *   2. Delete the <script> tag for it in layout/theme.liquid.
 * That's it — no other theme files are modified.
 *
 * To configure: edit the PRODUCTS list and GIFT_HANDLE at the top of the file.
 * ----------------------------------------------------------------------------
 */

(function () {
  'use strict';

  // ---------- CONFIG ----------------------------------------------------------
  // Each trigger product has a matching "bundle" copy product priced at the
  // Insider Club discount. Add more entries to enable the picker on more
  // products. Handles are the slug in the product URL: /products/<handle>.
  const PRODUCTS = [
    {
      trigger: 'neloria-calcium-eye-cream',
      bundle:  'neloria-calcium-eye-cream-copy',
      copy: {
        bareTitle: 'Just the eye cream',
        bareDesc:  '$39.95',
        vipTitle:  'Eye cream + free 30-day Insider Club trial',
        vipDesc:   '$19.99 today (save $19.96). Insider Club renews at $39.95/month after 30 days. Cancel anytime.',
      },
    },
    {
      trigger: 'neloria-magnesium-complex',
      bundle:  'neloria-magnesium-complex-copy',
      copy: {
        bareTitle: 'Just the magnesium',
        bareDesc:  '$39.95',
        vipTitle:  'Magnesium + free 30-day Insider Club trial',
        vipDesc:   '$19.99 today (save $19.96). Insider Club renews at $39.95/month after 30 days. Cancel anytime.',
      },
    },
    {
      trigger: 'neloria-lymphatic-drainage-drops',
      bundle:  'neloria-lymphatic-drainage-drops-1',
      copy: {
        bareTitle: 'Just the drops',
        bareDesc:  '$39.95',
        vipTitle:  'Drops + free 30-day Insider Club trial',
        vipDesc:   '$19.99 today (save $19.96). Insider Club renews at $39.95/month after 30 days. Cancel anytime.',
      },
    },
  ];

  // The product that gets auto-added (as a subscription) when the customer
  // picks the Insider Club option on any trigger product page.
  const GIFT_HANDLE = 'vip-membership';

  // Line item property kept for backward compatibility & cart introspection.
  // Leading underscore makes Shopify hide it from cart/checkout displays.
  const WANTS_VIP_PROPERTY = '_wants_vip';

  // Sections to re-render after a sync. These match section IDs in this theme.
  const SECTIONS_TO_REFRESH = [
    'cart-drawer',
    'cart-icon-bubble',
    'cart-notification-button',
    'cart-notification-product',
  ];

  // Set true to see logs in the browser console.
  // TEMPORARILY ON for diagnosing — set back to false once everything works.
  const DEBUG = true;

  // ---------- INTERNAL STATE --------------------------------------------------
  const log = (...args) => { if (DEBUG) console.log('[vip-auto-add]', ...args); };
  const warn = (...args) => console.warn('[vip-auto-add]', ...args);

  log('Script loaded. Configured products:', PRODUCTS.map(p => p.trigger).join(', '), '| Gift handle:', GIFT_HANDLE);

  // Re-entrancy guard: when WE call /cart/add.js or /cart/change.js to manage
  // the gift, we don't want our own request to trigger another sync.
  let syncInFlight = false;

  // Debounce timer for back-to-back cart mutations.
  let syncTimer = null;

  // Cached variant IDs (looked up once from product handles).
  // Each PRODUCTS[i] gets .triggerVariantIds and .bundleVariantIds attached
  // by resolveVariantIds(). The "all*" arrays below are flat unions across
  // every configured product, used by the cart-sync logic.
  let allTriggerVariantIds = null; // every regular variant across PRODUCTS
  let allBundleVariantIds  = null; // every bundle variant across PRODUCTS
  let giftVariantId = null;        // single variant ID (first available) of gift
  let giftSellingPlanId = null;    // subscription selling plan to use for the gift

  // ---------- VARIANT ID RESOLUTION ------------------------------------------
  // We resolve handles -> variant IDs at runtime so you don't need to hardcode
  // numeric IDs. Cached in sessionStorage to avoid refetching on every page.
  const CACHE_KEY = 'vip-auto-add:variant-cache:v8';

  function readCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Accept the cache only if it covers every configured product and the
      // gift, with non-empty variant arrays. This guards against stale data
      // from before a new product was added.
      if (!parsed || !parsed.gift) return null;
      if (!parsed.products || typeof parsed.products !== 'object') return null;
      for (const p of PRODUCTS) {
        const entry = parsed.products[p.trigger];
        if (!entry
            || !Array.isArray(entry.trigger) || !entry.trigger.length
            || !Array.isArray(entry.bundle)  || !entry.bundle.length) {
          return null;
        }
      }
      return parsed;
    } catch (e) { /* ignore */ }
    return null;
  }

  function writeCache() {
    try {
      const products = {};
      for (const p of PRODUCTS) {
        products[p.trigger] = {
          trigger: p.triggerVariantIds,
          bundle:  p.bundleVariantIds,
        };
      }
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        products,
        gift: giftVariantId,
        sellingPlan: giftSellingPlanId,
      }));
    } catch (e) { /* ignore quota/private-mode errors */ }
  }

  async function fetchProduct(handle) {
    const res = await fetch(`/products/${handle}.js`, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`Failed to fetch /products/${handle}.js (${res.status})`);
    return res.json();
  }

  function rebuildVariantUnions() {
    allTriggerVariantIds = [];
    allBundleVariantIds = [];
    for (const p of PRODUCTS) {
      if (p.triggerVariantIds) allTriggerVariantIds.push(...p.triggerVariantIds);
      if (p.bundleVariantIds)  allBundleVariantIds.push(...p.bundleVariantIds);
    }
  }

  async function resolveVariantIds() {
    if (allTriggerVariantIds && allBundleVariantIds && giftVariantId) return true;

    const cached = readCache();
    if (cached) {
      for (const p of PRODUCTS) {
        const entry = cached.products[p.trigger];
        p.triggerVariantIds = entry.trigger;
        p.bundleVariantIds  = entry.bundle;
      }
      giftVariantId     = cached.gift;
      giftSellingPlanId = cached.sellingPlan || null;
      rebuildVariantUnions();
      log('Using cached variant IDs', cached);
      return true;
    }

    log('Resolving variant IDs by fetching product JSON…');
    try {
      // Fetch every configured product in parallel, plus the gift.
      const handlesToFetch = [];
      for (const p of PRODUCTS) {
        handlesToFetch.push(['trigger', p, p.trigger]);
        handlesToFetch.push(['bundle',  p, p.bundle]);
      }
      handlesToFetch.push(['gift', null, GIFT_HANDLE]);

      const results = await Promise.all(handlesToFetch.map(([, , handle]) => fetchProduct(handle)));

      let giftProduct = null;
      for (let i = 0; i < handlesToFetch.length; i++) {
        const [role, productEntry] = handlesToFetch[i];
        const fetched = results[i];
        if (role === 'gift') { giftProduct = fetched; continue; }
        const ids = (fetched.variants || []).map(v => v.id);
        if (role === 'trigger') productEntry.triggerVariantIds = ids;
        else                    productEntry.bundleVariantIds  = ids;
        log(role, 'product loaded:', fetched.title, 'variants:', fetched.variants?.length);
      }
      log('Gift product loaded:', giftProduct?.title, 'variants:', giftProduct?.variants?.length);

      // Pick the first available variant of the gift; fall back to first.
      const giftVariants = (giftProduct && giftProduct.variants) || [];
      const firstAvailable = giftVariants.find(v => v.available);
      const chosenVariant = firstAvailable || giftVariants[0];
      giftVariantId = (chosenVariant || {}).id || null;

      // Extract the subscription selling plan ID for that variant.
      if (chosenVariant) {
        const allocations = chosenVariant.selling_plan_allocations || [];
        if (allocations.length > 0) giftSellingPlanId = allocations[0].selling_plan_id;
      }

      // Validate: every configured product must have at least one variant on
      // each side, and the gift must resolve to a real variant.
      const missing = [];
      for (const p of PRODUCTS) {
        if (!p.triggerVariantIds || !p.triggerVariantIds.length) missing.push(p.trigger);
        if (!p.bundleVariantIds  || !p.bundleVariantIds.length)  missing.push(p.bundle);
      }
      if (missing.length || !giftVariantId) {
        warn('Resolved products but some variant IDs are missing.',
             '\n  Missing variants for:', missing.length ? missing : 'none',
             '\n  Gift variant ID:', giftVariantId,
             '\n  → Check that all products listed above have at least one variant and are published to the Online Store sales channel.');
        return false;
      }

      if (!giftSellingPlanId) {
        warn('Gift product has no selling plan (subscription plan) attached.',
             '\n  → The script will add the gift as a one-time purchase, which will NOT get the 100% discount.',
             '\n  → Fix: in Seal Subscriptions, make sure the membership product has its subscription selling plan active.');
      }

      rebuildVariantUnions();
      writeCache();
      log('✓ Resolved variant IDs:', {
        products: PRODUCTS.map(p => ({ trigger: p.trigger, triggerVariantIds: p.triggerVariantIds, bundleVariantIds: p.bundleVariantIds })),
        giftVariantId,
        giftSellingPlanId,
      });
      return true;
    } catch (err) {
      warn('Could not resolve variant IDs.', err,
           '\n  → If you see a 404, check that every product listed in PRODUCTS plus the gift (' + GIFT_HANDLE + ') is published to the Online Store sales channel.');
      return false;
    }
  }

  // ---------- CART HELPERS ----------------------------------------------------
  function getCart() {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    }).then(r => r.json());
  }

  function findLine(cart, variantIds) {
    const ids = Array.isArray(variantIds) ? variantIds : [variantIds];
    return (cart.items || []).find(item => ids.includes(item.id));
  }

  function addGift() {
    const body = { id: giftVariantId, quantity: 1 };
    // Attach the subscription selling plan so Seal's 100%-off rule applies.
    // Without this, VIP is added as a one-time purchase at full price.
    if (giftSellingPlanId) body.selling_plan = giftSellingPlanId;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  }

  function setGiftQuantity(lineKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    });
  }

  // Direct add-to-cart for the trigger or bundle product. Bypasses the
  // theme's form entirely — used by our custom Add to Cart button on the
  // trigger product page so we have full control over which variant is added.
  function addProductToCart(variantId) {
    const body = { id: Number(variantId), quantity: 1 };
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  }

  // ---------- CART DRAWER -----------------------------------------------------
  // Once we open the drawer, we want it to stay open across section refreshes
  // (which replace the drawer's DOM, blowing away the 'active' class). This
  // flag tells refreshCartSections to re-open after re-rendering.
  let drawerShouldStayOpen = false;

  // Open the theme's cart drawer. Tries several strategies because the
  // theme's remote main.js doesn't expose a public API we can rely on:
  //   1. Call <cart-drawer>.open() if the method exists
  //   2. Dispatch the 'cart:open' event some themes listen for
  //   3. As a last resort, add the 'active' class directly to the drawer
  function openCartDrawer() {
    drawerShouldStayOpen = true;
    applyOpenStateToDrawer();
  }

  function applyOpenStateToDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      log('No <cart-drawer> element found; cannot open drawer.');
      return;
    }
    if (typeof drawer.open === 'function') {
      try { drawer.open(); log('Opened cart drawer via .open()'); return; }
      catch (e) { log('drawer.open() threw:', e); }
    }
    try {
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      log('Dispatched cart:open event');
    } catch (e) { /* noop */ }
    drawer.classList.add('active');
    document.body.classList.add('overflow-hidden');
    log('Added active class to cart drawer as fallback.');
  }

  // ---------- SECTION REFRESH ------------------------------------------------
  // After we modify the cart, the theme's UI is out of date. We use Shopify's
  // Section Rendering API to fetch fresh HTML for the cart drawer / icon
  // bubble and swap them into the DOM. This matches the pattern the theme's
  // own cart-notification.js uses.
  async function refreshCartSections() {
    const sectionsParam = SECTIONS_TO_REFRESH.join(',');
    try {
      const res = await fetch(`/?sections=${encodeURIComponent(sectionsParam)}`, {
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const data = await res.json();
      SECTIONS_TO_REFRESH.forEach(sectionId => {
        const html = data[sectionId];
        if (!html) return;
        // The shopify-section wrapper is identified by id="shopify-section-{id}"
        const target = document.getElementById(`shopify-section-${sectionId}`);
        if (target) {
          // The returned HTML is the section's outerHTML contents — replace innerHTML.
          target.innerHTML = parseInnerHTML(html);
        }
      });
      // If we opened the drawer earlier in this flow, re-open it now that
      // the cart-drawer DOM has been replaced.
      if (drawerShouldStayOpen) applyOpenStateToDrawer();
      // Let the theme know in case any listeners want to react.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (err) {
      log('Section refresh failed (non-fatal):', err);
    }
  }

  // The Sections API returns the rendered section HTML wrapped in its
  // shopify-section div. We need only the inner content to swap in.
  function parseInnerHTML(html) {
    const wrap = document.createElement('div');
    wrap.innerHTML = html.trim();
    const section = wrap.querySelector('.shopify-section');
    return section ? section.innerHTML : html;
  }

  // ---------- THE SYNC LOGIC --------------------------------------------------
  async function syncGift() {
    if (syncInFlight) { log('Sync already running, skipping'); return; }

    const ready = await resolveVariantIds();
    if (!ready) { log('Variant IDs not ready, skipping sync'); return; }

    syncInFlight = true;
    try {
      const cart = await getCart();
      const triggerLine = findLine(cart, allTriggerVariantIds); // any regular product
      const bundleLine  = findLine(cart, allBundleVariantIds);  // any bundle ($19.99) product
      const giftLine    = findLine(cart, giftVariantId);     // VIP Membership

      // VIP belongs in the cart whenever the bundle Neloria is in the cart.
      // The bundle variant is the signal that the customer chose the VIP
      // option — we know that because the script swaps to it only in that case.
      //
      // We also treat a legacy `_wants_vip=true` property on the regular
      // Neloria line as a valid signal, so customers whose cart was created
      // by an older version of the script (before we used the bundle product)
      // still get VIP added on their next visit.
      const hasLegacyVipFlag = !!(triggerLine
        && triggerLine.properties
        && String(triggerLine.properties[WANTS_VIP_PROPERTY]).toLowerCase() === 'true');

      const giftShouldBePresent = !!bundleLine || hasLegacyVipFlag;

      log('Cart state — regular Neloria present:', !!triggerLine,
          '| bundle Neloria present:', !!bundleLine,
          '| legacy VIP flag:', hasLegacyVipFlag,
          '| gift currently present:', !!giftLine,
          '| gift should be present:', giftShouldBePresent);

      let mutated = false;

      if (giftShouldBePresent && !giftLine) {
        // Trigger present + opted in, but gift missing — add the gift.
        log('Adding gift (variant ' + giftVariantId + ', selling_plan ' + giftSellingPlanId + ')');
        const res = await addGift();
        if (res.ok) {
          mutated = true;
          log('✓ Gift added successfully');
        } else {
          warn('Add-to-cart for gift FAILED with HTTP', res.status, '— body:', await safeBody(res),
               '\n  → A 422 usually means the VIP Membership variant is sold out, not published, or has inventory limits.',
               '\n  → A 404 means the variant ID does not exist on this store.');
        }

      } else if (!giftShouldBePresent && giftLine) {
        // Gift is there but shouldn't be (either trigger is gone, or
        // customer opted out / never opted in) — remove it.
        log('Removing gift (line key ' + giftLine.key + ')');
        const res = await setGiftQuantity(giftLine.key, 0);
        if (res.ok) {
          mutated = true;
          log('✓ Gift removed successfully');
        } else {
          warn('Removing gift FAILED with HTTP', res.status, '— body:', await safeBody(res));
        }

      } else if (giftShouldBePresent && giftLine && giftSellingPlanId
                 && giftLine.selling_plan_allocation?.selling_plan?.id !== giftSellingPlanId) {
        // Gift is in cart but as a one-time purchase (or wrong plan).
        // Remove and re-add with the correct selling plan so the 100%-off
        // subscription rule applies.
        log('Gift line has no/wrong selling plan → removing and re-adding as subscription');
        const removeRes = await setGiftQuantity(giftLine.key, 0);
        if (removeRes.ok) {
          const addRes = await addGift();
          if (addRes.ok) {
            mutated = true;
            log('✓ Gift converted to subscription line');
          } else {
            warn('Re-add as subscription FAILED with HTTP', addRes.status);
          }
        } else {
          warn('Remove (for re-add) FAILED with HTTP', removeRes.status);
        }

      } else if (giftShouldBePresent && giftLine && giftLine.quantity > 1) {
        // Gift was duplicated somehow — pin it back to qty 1.
        log('Gift quantity is', giftLine.quantity, '→ capping to 1');
        const res = await setGiftQuantity(giftLine.key, 1);
        if (res.ok) {
          mutated = true;
          log('✓ Gift quantity reset to 1');
        } else {
          warn('Capping gift quantity FAILED with HTTP', res.status);
        }

      } else {
        log('Cart state already correct');
      }

      if (mutated) await refreshCartSections();
    } catch (err) {
      console.warn('[vip-auto-add] Sync error:', err);
    } finally {
      syncInFlight = false;
    }
  }

  async function safeBody(res) {
    try { return await res.clone().text(); } catch (e) { return '(no body)'; }
  }

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncGift, 50);
  }

  // ---------- DETECTING CART MUTATIONS ----------------------------------------
  // We intercept fetch and XMLHttpRequest to detect when the theme (or any
  // app) hits a cart-mutation endpoint. After a successful mutation we
  // schedule a sync.
  const CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  function isCartMutationUrl(url) {
    if (!url) return false;
    const str = typeof url === 'string' ? url : (url.url || String(url));
    return CART_MUTATION_PATHS.some(p => str.includes(p));
  }

  // ----- fetch interception (for cart-mutation detection only) -----
  // These interceptors just watch for cart mutations to keep gift in sync.
  // The variant choice is made by the custom Add to Cart button, which calls
  // /cart/add.js directly with the right variant ID.
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url);
    const isMutation = isCartMutationUrl(url);

    const promise = originalFetch.apply(this, arguments);
    if (isMutation && !syncInFlight) {
      log('Detected cart mutation (fetch):', url);
      promise.then(res => {
        if (res && res.ok) scheduleSync();
      }).catch(() => { /* swallow */ });
    }
    return promise;
  };

  // ----- XHR interception (for cart-mutation detection only) -----
  const OriginalXHR = window.XMLHttpRequest;
  const originalOpen = OriginalXHR.prototype.open;
  const originalSend = OriginalXHR.prototype.send;

  OriginalXHR.prototype.open = function (method, url) {
    this.__vipAutoAddUrl = url;
    this.__vipAutoAddMethod = method;
    return originalOpen.apply(this, arguments);
  };

  OriginalXHR.prototype.send = function (body) {
    const url = this.__vipAutoAddUrl || '';
    if (isCartMutationUrl(url) && !syncInFlight) {
      log('Detected cart mutation (XHR):', url);
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) scheduleSync();
      });
    }
    return originalSend.call(this, body);
  };

  // ---------- ON-PAGE PICKER (PRODUCT PAGE ONLY) -----------------------------
  // On the trigger product's page, hide the theme's Bundle & Save block and
  // inject two custom radio options above the Add to Cart button. The
  // selected option determines whether the VIP property is attached to the
  // Neloria line item when the customer submits the form.
  //
  // Default: bare $39.95 (no VIP) so the trial is opt-in, not opt-out.

  // Returns the PRODUCTS entry whose trigger handle matches the current page
  // URL, or null if we're not on a trigger product page.
  function getCurrentProduct() {
    const path = window.location.pathname;
    for (const p of PRODUCTS) {
      if (path === '/products/' + p.trigger
          || path.startsWith('/products/' + p.trigger + '/')) {
        return p;
      }
    }
    return null;
  }

  function injectPickerStyles() {
    if (document.getElementById('vip-auto-add-styles')) return;
    const style = document.createElement('style');
    style.id = 'vip-auto-add-styles';
    style.textContent = `
      /* Hide the theme's Bundle & Save block on the trigger product page,
         since we're replacing it with our own picker. */
      .vip-trigger-page .bundle-offer { display: none !important; }

      .vip-picker {
        margin: 1.5rem 0;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        /* Inherit the theme's typography so the picker matches the rest of the page. */
        font-family: var(--font-body-family);
        font-style: var(--font-body-style);
        font-weight: var(--font-body-weight);
        color: rgb(var(--color-foreground));
      }
      .vip-picker, .vip-picker * {
        font-family: inherit;
      }
      .vip-picker__option {
        position: relative;
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 1rem 1.125rem;
        border: 2px solid rgba(0,0,0,0.12);
        border-radius: 0.75rem;
        cursor: pointer;
        transition: border-color 0.15s ease, background-color 0.15s ease;
      }
      .vip-picker__option:hover { border-color: rgba(0,0,0,0.35); }
      .vip-picker__option.is-selected {
        border-color: currentColor;
        background-color: rgba(0,0,0,0.03);
      }
      .vip-picker__radio {
        flex-shrink: 0;
        width: 1.25rem;
        height: 1.25rem;
        border: 2px solid currentColor;
        border-radius: 50%;
        margin-top: 0.125rem;
        position: relative;
        opacity: 0.6;
      }
      .is-selected .vip-picker__radio { opacity: 1; }
      .is-selected .vip-picker__radio::after {
        content: '';
        position: absolute;
        top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        width: 0.65rem; height: 0.65rem;
        background: currentColor;
        border-radius: 50%;
      }
      .vip-picker__body { flex: 1; min-width: 0; }
      .vip-picker__title {
        font-weight: var(--font-body-weight-bold);
        font-size: 0.95rem;
        line-height: 1.3;
        margin: 0 0 0.2rem 0;
      }
      .vip-picker__desc {
        font-size: 0.8rem;
        line-height: 1.4;
        opacity: 0.75;
        margin: 0;
      }
      .vip-picker__badge {
        display: inline-block;
        font-size: 0.7rem;
        font-weight: var(--font-body-weight-bold);
        background: #000;
        color: #fff;
        padding: 0.15rem 0.5rem;
        border-radius: 0.25rem;
        margin-left: 0.4rem;
        vertical-align: middle;
      }
      /* Native radios are invisible but still keyboard-focusable. */
      .vip-picker__option input[type="radio"] {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }
      .vip-picker__option:focus-within {
        outline: 2px solid currentColor;
        outline-offset: 2px;
      }
      /* On the trigger product page, hide the theme's Add to Cart and
         Buy It Now buttons. Our custom button replaces them. We use
         visibility:hidden + position:absolute so the form-validation
         logic the theme attaches to those buttons doesn't fire. */
      .vip-trigger-page .product-form__buttons > * {
        display: none !important;
      }
      /* Our custom Add to Cart button — styled to match the theme's
         .button .button--full-width. */
      .vip-atc-button {
        display: block;
        width: 100%;
        padding: 1rem 2rem;
        margin: 1rem 0 0;
        font-family: var(--font-body-family);
        font-style: var(--font-body-style);
        font-weight: var(--font-body-weight-bold);
        font-size: 1rem;
        letter-spacing: 0.1rem;
        text-transform: uppercase;
        color: rgb(var(--color-button-text));
        background: rgb(var(--color-button));
        border: none;
        border-radius: var(--buttons-radius, 0);
        cursor: pointer;
        min-height: 4.5rem;
        line-height: 1.2;
        transition: opacity 0.15s ease;
      }
      .vip-atc-button:hover:not(:disabled) { opacity: 0.85; }
      .vip-atc-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .vip-atc-button.is-loading {
        opacity: 0.7;
        cursor: wait;
      }
    `;
    document.head.appendChild(style);
  }

  function buildPickerElement(product) {
    const copy = product.copy;
    const wrap = document.createElement('div');
    wrap.className = 'vip-picker';
    wrap.setAttribute('role', 'radiogroup');
    wrap.setAttribute('aria-label', 'Purchase option');

    wrap.innerHTML = `
      <label class="vip-picker__option is-selected" data-vip-option="bare">
        <input type="radio" name="vip-picker-choice" value="bare" checked>
        <span class="vip-picker__radio" aria-hidden="true"></span>
        <span class="vip-picker__body">
          <span class="vip-picker__title">${escapeHtml(copy.bareTitle)}</span>
          <span class="vip-picker__desc">${escapeHtml(copy.bareDesc)}</span>
        </span>
      </label>
      <label class="vip-picker__option" data-vip-option="vip">
        <input type="radio" name="vip-picker-choice" value="vip">
        <span class="vip-picker__radio" aria-hidden="true"></span>
        <span class="vip-picker__body">
          <span class="vip-picker__title">${escapeHtml(copy.vipTitle)}<span class="vip-picker__badge">FREE TRIAL</span></span>
          <span class="vip-picker__desc">${escapeHtml(copy.vipDesc)}</span>
        </span>
      </label>
    `;

    // Click handler updates the selected styling.
    wrap.addEventListener('change', () => {
      wrap.querySelectorAll('.vip-picker__option').forEach(opt => {
        const radio = opt.querySelector('input[type="radio"]');
        opt.classList.toggle('is-selected', !!radio && radio.checked);
      });
      log('Picker selection changed:', getPickerValue());
    });

    return wrap;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    })[c]);
  }

  function getPickerValue() {
    const radio = document.querySelector('.vip-picker input[type="radio"]:checked');
    return radio ? radio.value : 'bare';
  }

  // (Form-swap approach removed — replaced with custom Add to Cart button
  // that calls /cart/add.js directly with the right variant ID.)

  function setupPicker() {
    const currentProduct = getCurrentProduct();
    if (!currentProduct) return;
    log('On trigger product page (' + currentProduct.trigger + ') — setting up picker.');

    // Mark the body so our CSS can hide the theme's bundle block.
    document.body.classList.add('vip-trigger-page');
    injectPickerStyles();

    // Find the main product form. The theme uses <product-form class="main-product-form">.
    // It may not be in the DOM yet if some sections render late, so retry briefly.
    let attempts = 0;
    const maxAttempts = 40; // ~4 seconds at 100ms intervals
    const interval = setInterval(() => {
      const form = document.querySelector('product-form.main-product-form form, form[action*="/cart/add"]');
      if (form) {
        clearInterval(interval);
        attachPickerToForm(form, currentProduct);
      } else if (++attempts >= maxAttempts) {
        clearInterval(interval);
        warn('Could not find product form to attach the VIP picker to.');
      }
    }, 100);
  }

  function attachPickerToForm(form, product) {
    if (form.dataset.vipPickerAttached === 'true') return;
    form.dataset.vipPickerAttached = 'true';

    // Insert the picker right before the Add to Cart button group.
    const buttonGroup = form.querySelector('.product-form__buttons') || form;
    const picker = buildPickerElement(product);
    buttonGroup.parentNode.insertBefore(picker, buttonGroup);
    log('Picker injected before button group.');

    // Hide the theme's Add to Cart + Buy It Now buttons. We replace them
    // with our own button that calls /cart/add.js directly with the right
    // variant ID, bypassing the theme's <product-form> web component entirely.
    const themeBtnsHider = document.createElement('style');
    themeBtnsHider.textContent = `
      .vip-trigger-page product-form.main-product-form .product-form__buttons,
      .vip-trigger-page .shopify-payment-button {
        display: none !important;
      }
    `;
    document.head.appendChild(themeBtnsHider);

    // Build our own Add to Cart button. Match the theme's styling by reusing
    // its .button and .button--full-width classes; the Aeon theme styles all
    // primary buttons consistently via those classes.
    const customBtn = document.createElement('button');
    customBtn.type = 'button';
    customBtn.className = 'button button--full-width button--primary vip-add-to-cart-btn';
    customBtn.style.cssText = 'margin-top: 1rem;';
    customBtn.innerHTML = '<span class="vip-add-to-cart-btn__label">Add to cart</span>';
    buttonGroup.parentNode.insertBefore(customBtn, buttonGroup);

    // Kick off variant resolution. The button is non-functional until this
    // completes; we put it in a loading state if it gets clicked too early.
    const resolvePromise = resolveVariantIds();
    let isResolved = false;
    resolvePromise.then(ok => {
      isResolved = !!ok;
      if (!ok) warn('Variant IDs failed to resolve — Add to Cart button will not work.');
      else log('Variant IDs ready. Custom Add to Cart button is live.');
    });

    customBtn.addEventListener('click', async function (event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      // Wait for resolution if it isn't done yet.
      if (!isResolved) {
        log('Variant resolution not yet complete — waiting before adding to cart.');
        customBtn.classList.add('loading');
        customBtn.disabled = true;
        await resolvePromise;
      }
      if (!product.triggerVariantIds || !product.triggerVariantIds.length) {
        warn('Cannot add to cart: trigger variant ID for', product.trigger, 'never resolved.');
        customBtn.classList.remove('loading');
        customBtn.disabled = false;
        return;
      }

      const wantsVip = getPickerValue() === 'vip';
      let variantToAdd;

      if (wantsVip) {
        if (!product.bundleVariantIds || !product.bundleVariantIds.length) {
          warn('Insider Club option selected but bundle variant for', product.trigger, 'not available; falling back to regular product.');
          variantToAdd = product.triggerVariantIds[0];
        } else {
          variantToAdd = product.bundleVariantIds[0];
        }
      } else {
        variantToAdd = product.triggerVariantIds[0];
      }

      log('Custom Add to Cart firing with variant', variantToAdd, '(wantsVip:', wantsVip + ', product:', product.trigger + ')');
      customBtn.classList.add('loading');
      customBtn.disabled = true;

      try {
        const res = await addProductToCart(variantToAdd);
        if (!res.ok) {
          warn('Add to cart failed:', res.status, await res.text());
          customBtn.classList.remove('loading');
          customBtn.disabled = false;
          return;
        }
        // The fetch interception below will catch this cart mutation and
        // run scheduleSync(), which auto-adds VIP if needed and refreshes
        // the cart drawer UI.
        log('Add to cart succeeded; sync will run shortly.');
        // Open the cart drawer. Theme's <product-form> normally does this
        // automatically; since we bypassed it, we need to trigger it ourselves.
        openCartDrawer();
      } catch (err) {
        warn('Add to cart errored:', err);
      } finally {
        // Re-enable the button after a short delay so the customer can add
        // another if they want.
        setTimeout(() => {
          customBtn.classList.remove('loading');
          customBtn.disabled = false;
        }, 800);
      }
    });
  }

  // ---------- INITIAL SYNC ON PAGE LOAD --------------------------------------
  // Handles the case where the customer arrives with a stale cart from
  // another tab/session (e.g. trigger product was removed elsewhere).
  function init() {
    setupPicker();
    // Warm the variant cache early, then do one sync pass.
    resolveVariantIds().then(ok => { if (ok) scheduleSync(); });

    // Reset the "keep drawer open" flag when the customer closes the drawer
    // themselves. We watch for the 'active' class disappearing from
    // <cart-drawer>. The theme adds/removes that class when toggling.
    function watchDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) { setTimeout(watchDrawer, 200); return; }
      const observer = new MutationObserver(() => {
        if (drawerShouldStayOpen && !drawer.classList.contains('active')) {
          drawerShouldStayOpen = false;
          log('Customer closed cart drawer — resetting open flag.');
        }
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    watchDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
